/**
 * Rodeo-only scoring hooks.
 *
 * The advanced scoring UI carried over from jackpots talks to
 * /api/scoring-ops (now polymorphic, see scoring.service.ts) and
 * /api/draw-ops (also polymorphic for teams). The hooks here surface
 * the *rodeo-specific* primitives that have no jackpot equivalent:
 *
 *   - eligibility checks (draft / published / finalized)
 *   - the rodeo-wide scoring_state machine (sandbox / live)
 *   - the per-performance live-publish toggle
 *   - flip-to-live (with dry-run preview) and sandbox reset
 *   - the ordered performance_events list used to label the round picker
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

export type ScoringMode = 'sandbox' | 'live';
export type DrawState = 'draft' | 'published' | 'finalized' | 'missing';

export interface EventEligibility {
  event_id: string;
  event_name: string | null;
  draw_state: DrawState;
  execution_id: string | null;
  published_at: string | null;
  finalized_at: string | null;
  sandbox_allowed: boolean;
  live_allowed: boolean;
  blocked_reason: string | null;
  // Per-event scoring state + visibility flags.
  scoring_state: string;
  show_results: boolean;
  is_current_live_event: boolean;
  // Live-scoring progress: contestants with a recorded run vs contestants
  // drawn into the event's latest execution. Populated by the eligibility list.
  scored_count?: number;
  total_count?: number;
}

export interface EligibilityResponse {
  rodeo: { id: string; name: string | null; scoring_state: string; draw_mode: string };
  events: EventEligibility[];
}

export type RunOrderMode = 'draw' | 'ytd_desc' | 'ytd_asc' | 'current_desc' | 'current_asc';

/** The delegate's picker, in the order it lists the modes. */
export const RUN_ORDER_OPTIONS: Array<{ value: RunOrderMode; label: string }> = [
  { value: 'draw', label: 'Draw order' },
  { value: 'ytd_desc', label: 'YTD winnings: high \u2192 low' },
  { value: 'ytd_asc', label: 'YTD winnings: low \u2192 high' },
  { value: 'current_desc', label: 'This rodeo: high \u2192 low' },
  { value: 'current_asc', label: 'This rodeo: low \u2192 high' },
];

/** The note shown under the board so nobody mistakes the order for the draw. */
export function runOrderNote(mode: RunOrderMode | undefined | null): string | null {
  switch (mode) {
    case 'ytd_desc':
      return 'Ordered by YTD winnings, highest first';
    case 'ytd_asc':
      return 'Ordered by YTD winnings, lowest first';
    case 'current_desc':
      return 'Ordered by current winnings (YTD + this rodeo so far), highest first';
    case 'current_asc':
      return 'Ordered by current winnings (YTD + this rodeo so far), lowest first';
    default:
      return null;
  }
}

export interface RodeoEventPerformance {
  id: string;
  performance_id: string;
  event_id: string;
  performance_name: string | null;
  start_datetime: string | null;
  is_scoring_published_live: boolean;
  /** The round this performance runs (rodeo_round_configs, else chronological). */
  round_number: number;
  /** The configured round name; null when the chronological fallback named it. */
  round_label?: string | null;
  /** Finals only: how this round's run order is derived. Defaults to 'draw'. */
  run_order_mode?: RunOrderMode;
}

export interface RodeoEventPerformancesResponse {
  data: RodeoEventPerformance[];
  /**
   * True when the event's rodeo is a finals: every contestant runs every
   * performance, so one performance IS one round and the scoring board shows
   * round tabs instead of a performance x rounds grid.
   */
  is_finals?: boolean;
}

export type ScoringStateValue = 'not_started' | 'sandbox' | 'live' | 'completed';

export interface LiveLeaderboardEntry {
  registration_id: string;
  place: number | null;
  contestant_name: string | null;
  partner_name: string | null;
  final_time: number | null;
  final_score: number | null;
  penalty_seconds: number;
  is_disqualified: boolean;
  no_time: boolean;
  /** True when the contestant did not perform (DNP) — sorts below NT/NS. */
  did_not_perform?: boolean;
}

export interface CurrentLiveEventResponse {
  rodeo_id: string;
  event: {
    id: string;
    event_name: string | null;
    scoring_state: string;
    show_results: boolean;
  } | null;
  mode: ScoringMode | null;
  performance_events: Array<{
    id: string;
    performance_id: string;
    performance_name: string | null;
    start_datetime: string | null;
    is_published_live: boolean;
  }>;
  entries: LiveLeaderboardEntry[];
}

export const rodeoScoringKeys = {
  all: ['rodeo-scoring-ops'] as const,
  eligibility: (rodeoId: string) => [...rodeoScoringKeys.all, 'eligibility', rodeoId] as const,
  eventPerformances: (eventId: string) => [...rodeoScoringKeys.all, 'event-performances', eventId] as const,
  currentLiveEvent: (rodeoId: string) => [...rodeoScoringKeys.all, 'current-live-event', rodeoId] as const,
};

export function useRodeoScoringEligibility(rodeoId: string | undefined) {
  return useQuery({
    queryKey: rodeoId
      ? rodeoScoringKeys.eligibility(rodeoId)
      : ['rodeo-scoring-ops', 'eligibility', 'noop'],
    queryFn: () => httpClient.get<EligibilityResponse>(`/rodeo-scoring-ops/rodeo/${rodeoId}/eligibility`),
    enabled: !!rodeoId,
  });
}

export function useRodeoEventPerformances(eventId: string | undefined) {
  return useQuery({
    queryKey: eventId
      ? rodeoScoringKeys.eventPerformances(eventId)
      : ['rodeo-scoring-ops', 'event-performances', 'noop'],
    queryFn: () =>
      httpClient.get<RodeoEventPerformancesResponse>(
        `/rodeo-scoring-ops/event/${eventId}/performance-events`,
      ),
    enabled: !!eventId,
  });
}

/** The rodeo's public live-event view (current event + its leaderboard). */
export function useRodeoCurrentLiveEvent(
  rodeoId: string | undefined,
  options?: { refetchInterval?: number },
) {
  return useQuery({
    queryKey: rodeoId
      ? rodeoScoringKeys.currentLiveEvent(rodeoId)
      : ['rodeo-scoring-ops', 'current-live-event', 'noop'],
    queryFn: () =>
      httpClient.get<CurrentLiveEventResponse>(
        `/rodeo-scoring-ops/rodeo/${rodeoId}/current-live-event`,
      ),
    enabled: !!rodeoId,
    refetchInterval: options?.refetchInterval,
  });
}

/** Set one event's scoring phase (sandbox ⇄ live ⇄ completed). No data wipe. */
export function useSetEventScoringState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      scoring_state,
    }: {
      eventId: string;
      scoring_state: ScoringStateValue;
    }) =>
      httpClient.post(`/rodeo-scoring-ops/event/${eventId}/scoring-state`, {
        scoring_state,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rodeoScoringKeys.all });
      qc.invalidateQueries({ queryKey: ['scoring-ops'] });
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
    },
  });
}

/** Mode-aware reset: clears only the runs for the given mode of one event. */
export function useResetEventScores() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, mode }: { eventId: string; mode: ScoringMode }) =>
      httpClient.post(`/rodeo-scoring-ops/event/${eventId}/reset`, { mode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rodeoScoringKeys.all });
      qc.invalidateQueries({ queryKey: ['scoring-ops'] });
    },
  });
}

/** Toggle whether results render on the contestant-facing event detail page. */
export function useSetShowResults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, show_results }: { eventId: string; show_results: boolean }) =>
      httpClient.patch(`/rodeo-scoring-ops/event/${eventId}/show-results`, {
        show_results,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rodeoScoringKeys.all });
      // The event detail page reads show_results off the events record.
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/** Mark/clear the event shown on the rodeo's public live-event page. */
export function useSetCurrentLiveEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      is_current_live_event,
    }: {
      eventId: string;
      is_current_live_event: boolean;
    }) =>
      httpClient.patch(`/rodeo-scoring-ops/event/${eventId}/current-live-event`, {
        is_current_live_event,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rodeoScoringKeys.all });
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/**
 * Set how one finals round's contestants are ordered to run.
 *
 * Optimistic: the picker is the operator's own action and the round tabs are
 * already on screen, so the new value lands immediately and the roster query
 * (which carries run_position and the winnings columns) is refetched behind it.
 */
export function useSetRunOrderMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      roundNumber,
      run_order_mode,
    }: {
      eventId: string;
      roundNumber: number;
      run_order_mode: RunOrderMode;
    }) =>
      httpClient.put(`/rodeo-scoring-ops/event/${eventId}/round/${roundNumber}/run-order`, {
        run_order_mode,
      }),
    onMutate: async ({ eventId, roundNumber, run_order_mode }) => {
      const key = rodeoScoringKeys.eventPerformances(eventId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<RodeoEventPerformancesResponse>(key);
      if (previous?.data) {
        qc.setQueryData<RodeoEventPerformancesResponse>(key, {
          ...previous,
          data: previous.data.map((perf) =>
            perf.round_number === roundNumber ? { ...perf, run_order_mode } : perf,
          ),
        });
      }
      return { key, previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(context.key, context.previous);
    },
    onSettled: (_data, _err, _vars, context) => {
      if (context?.key) qc.invalidateQueries({ queryKey: context.key });
      // The roster carries run_position + the winnings columns.
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
    },
  });
}
